import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'

import type { DualControlResult } from '@/api/types'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AdminDualControlNotice, AdminDualControlOutcome } from '@/features/admin/dual-control'
import { useCreateUser } from '@/features/admin/mutations'
import { ADMIN_PERMISSIONS, useAdminPermissions } from '@/features/admin/permissions'
import { UserCreateForm } from '@/features/admin/user-create-form'

/**
 * Onboarding a staff account.
 *
 * Dual-controlled, and it answers with a {@link DualControlResult} rather than the created account
 * — a request awaiting a countersignature has not created one. There is no username in the outcome
 * for that reason, the way `NewGroupDialog` cannot show a group code for the same reason.
 */
export function AdminUserNewPage() {
  const navigate = useNavigate()
  const permissions = useAdminPermissions()
  const create = useCreateUser()
  const [result, setResult] = useState<DualControlResult | null>(null)

  return (
    <RequiresWiderScreen
      screen="Onboarding a staff account"
      reason="It sets roles, branch access, and account flags that every future request checks. Rarely urgent, permanently consequential."
    >
      <Link
        to="/admin/users"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All users
      </Link>

      <PageHeader
        title="New staff account"
        description="Nothing can sign in on it until a second person countersigns it — and it is issued no credential even then."
      />

      {permissions.canCreateUser ? (
        <UserCreateForm
          submitLabel="Submit the account"
          isPending={create.isPending}
          error={create.isError ? create.error : null}
          onDone={() => void navigate({ to: '/admin/users' })}
          notice={
            <AdminDualControlNotice
              action="Creating this account"
              approvePermission={ADMIN_PERMISSIONS.userCreateApprove}
              canCountersign={permissions.canCountersignCreateUser}
            />
          }
          outcome={
            result ? (
              <AdminDualControlOutcome
                result={result}
                awaitingDescription="The account is stored and does not exist yet. Nobody can sign in on it, and it will not appear in the directory."
                appliedDescription="The account exists. It carries the roles chosen above and no password — issue a temporary one from its detail screen before handing it over."
              />
            ) : null
          }
          onSubmit={(request) => {
            create.mutate(request, { onSuccess: setResult })
          }}
        />
      ) : (
        <Alert tone="warning">
          <AlertTitle>Your role cannot create staff accounts</AlertTitle>
          <AlertDescription>
            This needs <span className="font-mono text-xs">{ADMIN_PERMISSIONS.userCreate}</span>,
            and a second person holding{' '}
            <span className="font-mono text-xs">{ADMIN_PERMISSIONS.userCreateApprove}</span> to
            countersign it.
          </AlertDescription>
        </Alert>
      )}
    </RequiresWiderScreen>
  )
}
